import React, { useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ToitEnPenteAcousticLayerTypes } from "@iko-design-center/shared";
import { styles } from "../../../styles";
import { P } from "../../common/Typography";

const Container = styled.div<{ background: boolean }>`
  background: ${(props) =>
    props.background ? styles.colors.darkBlue5 : "unset"};
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;

  & > div {
    &:nth-of-type(even) {
      background: ${styles.colors.white};
    }
  }
`;
export const AcousticPerformance = ({ acousticLayer, thickness }: any) => {
  const { t } = useTranslation() as any

  const selection = useMemo(() => {
    if ([ToitEnPenteAcousticLayerTypes.DB_38, ToitEnPenteAcousticLayerTypes.DB_40].includes(acousticLayer)) {
      if (90 <= thickness && thickness <= 132) return ToitEnPenteAcousticLayerTypes.DB_38
      if (thickness >= 133) return ToitEnPenteAcousticLayerTypes.DB_40
    } else{
      if (90 <= thickness && thickness <= 132) return ToitEnPenteAcousticLayerTypes.DB_43
      if (thickness >= 133) return ToitEnPenteAcousticLayerTypes.DB_44
    }

    return acousticLayer
  }, [thickness, acousticLayer])

  const renderRows = () => {
    if ([ToitEnPenteAcousticLayerTypes.DB_38, ToitEnPenteAcousticLayerTypes.DB_40].includes(selection)) {
      return (<>
        <Row
          title={t('toitEnPente.overview.acousticPerformance.lowTitle')}
          value={ToitEnPenteAcousticLayerTypes.DB_38.split(' ')[0]}
          unit={t('sol.overview.acousticPerformance.unit')}
          selected={selection === ToitEnPenteAcousticLayerTypes.DB_38}
        />
        <Row
          title={t('toitEnPente.overview.acousticPerformance.highTitle')}
          value={ToitEnPenteAcousticLayerTypes.DB_40.split(' ')[0]}
          unit={t('sol.overview.acousticPerformance.unit')}
          selected={selection === ToitEnPenteAcousticLayerTypes.DB_40}
        />
      </>)
    } else {
      return (<>
        <Row
          title={t('toitEnPente.overview.acousticPerformance.lowTitle')}
          value={ToitEnPenteAcousticLayerTypes.DB_43.split(' ')[0]}
          unit={t('sol.overview.acousticPerformance.unit')}
          selected={selection === ToitEnPenteAcousticLayerTypes.DB_43}
        />
        <Row
          title={t('toitEnPente.overview.acousticPerformance.highTitle')}
          value={ToitEnPenteAcousticLayerTypes.DB_44.split(' ')[0]}
          unit={t('sol.overview.acousticPerformance.unit')}
          selected={selection === ToitEnPenteAcousticLayerTypes.DB_44}
        />
      </>)

    }
  }

  return (
    <Container background={true}>
      {renderRows()}
    </Container>
  );
};

const ValueContainer = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-direction: Row;
  border-left: ${(props) =>
    props.selected ? `3px solid ${styles.colors.black}` : `unset`};
  padding-inline: ${(props) => props.theme.designHeight(16)}px;
  padding-top: ${(props) => props.theme.designHeight(12)}px;
  padding-bottom: ${(props) => props.theme.designHeight(12)}px;
  min-height: 61px;

  .selected {
    font-size: 9px;
    line-height: 14px;
    font-weight: 700;
    margin-bottom: 7px;
    display: block;
    color: ${styles.colors.black};
    text-transform: uppercase;
  }

  @media screen and (pointer: coarse) {
    p {
      font-size: 13px;
      line-height: 16px;
    }
  }
`;

const ValueItem = styled.div`
  p {
    margin-top: 0;
    display: flex;
    align-items: center;
  }

  .unit {
    margin-left: 6px;
    white-space: nowrap;
    text-transform: lowercase;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }

  @media screen and (pointer: coarse) {
    p {
      font-size: 18px;
    }

    .unit {
      font-size: 13px;
    }
  }
`;

const Row = ({
  title,
  value,
  unit,
  selected,
  extra,
}: {
  title: string;
  value: any;
  unit: string;
  selected: boolean;
  extra?: string;
}) => {
  const { t } = useTranslation() as any
  return (
    <ValueContainer selected={selected}>
      <div>
        {selected &&
          <span className="selected">{t('sol.overview.acousticPerformance.selection')}</span>
        }
        <P fontSizePx={13} color={styles.colors.dark2} lineHeightPx={16} innerHTML={title} fontWeight={400}>
          {title}
        </P>
        {extra &&
          <P fontSizePx={10} color={styles.colors.dark2} lineHeightPx={14} fontWeight={400}>
            + {extra}
          </P>
        }
      </div>
      <ValueItem>
        <P fontSizePx={24} color={styles.colors.dark2} lineHeightPx={24} fontWeight={700}>
          {value} <span className="unit">{unit}</span>
        </P>
      </ValueItem>
    </ValueContainer>
  );
};
